import React, { useMemo } from "react";

import moment from "moment";
import { TFunction, useTranslation } from "react-i18next";

import { Table } from "antd";
import { SortOrder } from "antd/es/table/interface";
import { Key, SorterResult, TableCurrentDataSource, TablePaginationConfig } from "antd/lib/table/interface";

import { ColumnTitle } from "../../../../components/table";
import { IntegrationErrorItem, IntegrationErrorType } from "../../../../store/integration_errors/types";
import { formatNumber, getSortOrder } from "../../../../utils/table";

import { DebugLink } from "./debug_link";

export const optionsErrorsType: IntegrationErrorType[] = [
  "unknown_error",
  "internal_error",
  "parse_error",
  "timeout_error",
  "partner_error",
  "request_error",
];

const columns = (
  t: TFunction<"integration_errors_page">,
  ordering: string,
  timestampFmt: string,
  errorTypeFilter: IntegrationErrorType[]
) => {
  return [
    {
      key: "timestamp",
      title: <ColumnTitle title={t("table.columns.timestamp.title")} />,
      dataIndex: "timestamp",
      sorter: true,
      sortOrder: getSortOrder("timestamp", ordering),
      showSorterTooltip: false,
      render: (value: string) => moment(value).format(timestampFmt),
    },
    {
      key: "errorType",
      title: <ColumnTitle title={t("table.columns.errorType.title")} />,
      dataIndex: "errorType",
      render: (errorType: number) => t(`enums.errorType.${errorType}`),
      filteredValue: errorTypeFilter,
      filters: optionsErrorsType.map((errorType) => ({
        text: t(`enums.errorType.${errorType}`),
        value: errorType,
      })),
    },
    {
      key: "errors",
      title: <ColumnTitle title={t("table.columns.errors.title")} />,
      dataIndex: "errors",
      render: (errors: number) => formatNumber(errors),
    },
    {
      key: "percentByTotalErrors",
      title: <ColumnTitle title={t("table.columns.percentByTotalErrors.title")} />,
      dataIndex: "percentByTotalErrors",
      render: (percentByTotalErrors: number) => `${formatNumber(percentByTotalErrors)} %`,
    },
    {
      key: "percentByTotalSearches",
      title: <ColumnTitle title={t("table.columns.percentByTotalSearches.title")} />,
      dataIndex: "percentByTotalSearches",
      render: (percentByTotalSearches: number) => `${formatNumber(percentByTotalSearches)} %`,
    },
    {
      key: "debugger",
      render: (record: IntegrationErrorItem) => <DebugLink errorType={record.errorType} />,
    },
  ];
};

export interface DataTableProps {
  total: number;
  timestampFmt: string;
  errorTypeFilter: IntegrationErrorType[];
  dataSource: IntegrationErrorItem[];
  ordering: string;
  currentPage: number;
  pageSize?: number;
  loading?: boolean;
  onChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<IntegrationErrorItem> | SorterResult<IntegrationErrorItem>[],
    extra: TableCurrentDataSource<IntegrationErrorItem>
  ) => void;
}

export const IntegrationErrorsTable: React.FC<DataTableProps> = (props) => {
  const { t } = useTranslation("integration_errors_page");

  const tableColumns = useMemo(() => {
    return columns(t, props.ordering, props.timestampFmt, props.errorTypeFilter);
  }, [props.errorTypeFilter, props.ordering, props.timestampFmt, t]);

  return (
    <Table
      bordered
      loading={props.loading}
      columns={tableColumns}
      sortDirections={["ascend", "descend", "ascend"] as SortOrder[]}
      dataSource={props.dataSource}
      onChange={props.onChange}
      rowKey={(record: IntegrationErrorItem) => `integration-error-row-${record.timestamp}-${record.errorType}`}
      pagination={{
        total: props.total,
        current: props.currentPage,
        showSizeChanger: true,
        pageSizeOptions: ["10", "25", "50", "100"],
        pageSize: props.pageSize,
      }}
      size="small"
    />
  );
};
